<template>
    <div
        :class="styleClass"
        class="box box-chart">
        <div class="box-header with-border">
            <h3 class="box-title">{{ title }}</h3>
            <div class="box-tools pull-right">
                <label
                    v-if="unknownSwitch"
                    class="form-switch">
                    <input
                        v-model="unknown"
                        type="checkbox">
                    <i/>
                    brez "ni podatka"
                </label>
            </div>
        </div>
        <div class="box-body">
            <div class="row">
                <div class="col-xs-12">
                    <div
                        :id="chartId"
                        :style="{height: '340px'}"/>
                </div>
                <div class="col-xs-12">
                    <table
                        v-if="stats"
                        class="table table-striped table-bordered">
                        <thead>
                            <tr>
                                <th colspan="1">{{ columns[0] }}</th>
                                <th colspan="1">{{ columns[1] }}</th>
                                <th colspan="1">odstotek</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr
                                v-for="stat in tableData"
                                :key="stat[0]">
                                <td colspan="1">{{ stat[0] }}</td>
                                <td colspan="1">{{ Number(stat[1]).toLocaleString('sl-SI') }}</td>
                                <td colspan="1">{{ Number(stat[1] / total * 100).toLocaleString('sl-SI',
                                                                                                {maximumFractionDigits: 2}) }} %
                                </td>
                            </tr>
                        </tbody>
                        <tfoot>
                            <tr>
                                <th colspan="2">SKUPAJ</th>
                                <th colspan="1">{{ Number(total).toLocaleString('sl-SI') }}</th>
                            </tr>
                        </tfoot>
                    </table>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import {GoogleCharts} from '../slovenianGoogleCharts';

export default {
    name: 'Chart',

    props: {
        apiUrl: {
            required: true,
            type:     String
        },

        title: {
            required: true,
            type:     String
        },

        columns: {
            required: true,
            type:     Array
        },

        styleClass: {
            type:    String,
            default: 'box-default'
        },

        maxTableRows: {
            type:    Number,
            default: 6
        },

        unknownSwitch: {
            type:    Boolean,
            default: false
        }
    },

    data() {
        return {
            chartId:      null,
            chartData:    [],
            stats:        null,
            windowHeight: 0,
            windowWidth:  0,
            pieHeight:    400,
            pieWidth:     640,
            zoomFactor:   0.85,
            unknown:      false
        };
    },

    computed: {
        total() {
            if (this.chartData.length > 0) {
                return this.chartData
                    .map(el => Number(el[1]))
                    .reduce((sum, el) => {
                        return sum + el;
                    }, 0);
            }

            return 1;
        },

        tableData() {
            let sortedKeys = Object.keys(this.stats)
                .sort((a, b) => this.stats[b] - this.stats[a]);

            let tableData = sortedKeys.slice(0, 1 + this.maxTableRows)
                .map(key => [key, this.stats[key]]);

            if (sortedKeys.length > this.maxTableRows) {
                tableData.push(['drugo', sortedKeys.slice(1 + this.maxTableRows, -1)
                    .reduce((sum, key) => {
                        return sum + Number(this.stats[key]);
                    }, 0)]);
            }

            return tableData;
        }
    },

    watch: {
        chartData(current, previous) {
            if (current.length > 0 && current !== previous) {
                GoogleCharts.load(this.drawChart);
            }
        },

        windowWidth() {
            if (this.chartData.length > 0) {
                GoogleCharts.load(this.drawChart);
            }
        },

        unknown(current, previous) {
            if (current !== previous) {
                this.getData(current);

                GoogleCharts.load(this.drawChart);
            }
        }
    },

    mounted() {
        this.chartId = this._uid;

        this.$nextTick(() => {
            window.addEventListener('resize', () => {
                this.windowHeight = window.innerHeight;
                this.windowWidth = window.innerWidth;
            });
        });

        this.getData();
    },

    methods: {
        drawChart() {
            let chart = new GoogleCharts.api.visualization.PieChart(document.getElementById(this.chartId)),
                data  = GoogleCharts.api.visualization.arrayToDataTable([this.columns].concat(this.chartData)),
                view  = new GoogleCharts.api.visualization.DataView(data);
            chart.draw(view, {
                legend:       {
                    position:  'right',
                    alignment: 'center'
                },
                pieSliceText: 'label',
                is3D:         true,
                chartArea:    {
                    width:  this.pieWidth * this.zoomFactor,
                    height: this.pieHeight * this.zoomFactor
                }
            });
        },

        getData(unknown = false) {
            axios.get(this.apiUrl, {params: unknown ? {no_unknown : 1} : {}})
                .then(({data}) => {
                    this.$set(this, 'stats', data);
                    this.$set(this, 'chartData', Object.keys(data).map(key => [key, Number(data[key])]));
                })
                .catch(error => {
                    this.$toast.error({
                        title:   'NAPAKA!',
                        message: error.response.data.message,
                        timeOut: 10000
                    });
                });
        }
    }
};
</script>

<style scoped>
    .table tfoot tr {
        border-top: 1px solid #666;
        background-color: rgba(0, 0, 0, 0.1);
    }

    .form-switch {
        display: inline-block;
        cursor: pointer;
        -webkit-tap-highlight-color: transparent;
    }

    .form-switch i {
        position: relative;
        display: inline-block;
        margin-right: .5rem;
        width: 46px;
        height: 26px;
        background-color: #e6e6e6;
        border-radius: 23px;
        vertical-align: text-bottom;
        transition: all 0.3s linear;
    }

    .form-switch i::before {
        content: "";
        position: absolute;
        left: 0;
        width: 42px;
        height: 22px;
        background-color: #fff;
        border-radius: 11px;
        transform: translate3d(2px, 2px, 0) scale3d(1, 1, 1);
        transition: all 0.25s linear;
    }

    .form-switch i::after {
        content: "";
        position: absolute;
        left: 0;
        width: 22px;
        height: 22px;
        background-color: #fff;
        border-radius: 11px;
        box-shadow: 0 2px 2px rgba(0, 0, 0, 0.24);
        transform: translate3d(2px, 2px, 0);
        transition: all 0.2s ease-in-out;
    }

    .form-switch:active i::after {
        width: 28px;
        transform: translate3d(2px, 2px, 0);
    }

    .form-switch:active input:checked + i::after {
        transform: translate3d(16px, 2px, 0);
    }

    .form-switch input {
        display: none;
    }

    .form-switch input:checked + i {
        background-color: #4BD763;
    }

    .form-switch input:checked + i::before {
        transform: translate3d(18px, 2px, 0) scale3d(0, 0, 0);
    }

    .form-switch input:checked + i::after {
        transform: translate3d(22px, 2px, 0);
    }
</style>


// WEBPACK FOOTER //
// resources/assets/js/components/Statistics/Chart.vue